/**
 * This custom hook provides the functionality to retrieve users to be shown in the UI.
 */

import React, {useContext, useEffect, useState} from "react";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useSnackbar} from "notistack";
import ScoredSceneSummarySlider from "../utils/sliders/ScoredSceneSummarySlider";
import CoursePlaylistCard from "../../courses/card/playlist/CoursePlaylistCard";
import {useConfirm} from "material-ui-confirm";
import {useIntl} from "react-intl";

const useListScoredScenes = (videoKey, onScoredScenesUpdated) => {

    // State storage of the users shown in the table
    const [scoredScenes, setScoredScenes] = useState([])

    // Loading indication
    const [loading, setLoading] = useState(true)
    const [deletionInProgress, setDeletionInProgress] = useState(false)
    const [deletionAttemptedScoredScene, setDeletionAttemptedScoredScene] = useState(null)

    const {signedInUser} = useContext(FirebaseAuthContext);

    const {enqueueSnackbar} = useSnackbar();
    const confirm = useConfirm();

    // Internationalization for simple strings
    const intl = useIntl();

    // Retrieving the already existing videos
    const fetchScoredScenes = async () => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/videos/' + videoKey + '/scoredScenes?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                const scoredScenesJson = await data.json();
                setScoredScenes(scoredScenesJson)
                if (onScoredScenesUpdated) {
                    onScoredScenesUpdated(scoredScenesJson)
                }
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.scoredScene.messages.list.error',
                        defaultMessage: 'Something went wrong while listing the video scenes: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        fetchScoredScenes();
    }, [])

    const handleDeleteScoredScene = (scoredScene) => {
        setDeletionAttemptedScoredScene(scoredScene.key)
        return deletionAllowed(scoredScene)
            .then((checkResult) => {
                setDeletionAttemptedScoredScene(null)
                if (checkResult.allowed) {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.scoredScene.dialog.delete.confirm.title',
                            defaultMessage: 'Delete Video Scene'
                        }),
                        content: <div>
                            {intl.formatMessage({
                                id: 'app.scoredScene.dialog.delete.confirm.description',
                                defaultMessage: 'Delete video scene?'
                            })}
                            <ScoredSceneSummarySlider scoredScene={scoredScene} timeFormat="mm:ss:SS"/>
                        </div>
                    }).then(() => 'OK').catch(() => null)
                } else {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.scoredScene.dialog.delete.existingReferences.title',
                            defaultMessage: 'Deletion not allowed'
                        }),
                        content: <div>
                            <p>{intl.formatMessage({
                                id: 'app.scoredScene.dialog.delete.existingReferences.description',
                                defaultMessage: 'The video scene is included in the following playlists'
                            })}:</p>
                            <div>
                                {checkResult.playlists.map((playlist) => <CoursePlaylistCard key={playlist.key} playlist={playlist}/>)}
                            </div>
                        </div>
                    }).then(() => null).catch(() => null)
                }
            })
            .then((clickedButton) => {
                if (clickedButton === 'OK') {
                    return deleteScoredScene(scoredScene)
                }
                return null
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.scoredScene.messages.delete.error',
                        defaultMessage: 'Something went wrong while deleting the video scene: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setDeletionAttemptedScoredScene(null)
                setDeletionInProgress(false)
            })
    }

    const deletionAllowed = async (scoredScene) => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/videos/' + videoKey + '/scoredScenes/' + scoredScene.key + '/deletionAllowed?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }
                return data.json();
            })
    }

    const deleteScoredScene = async (scoredScene) => {
        setDeletionInProgress(true)
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/videos/' + videoKey + '/scoredScenes/' + scoredScene.key, {
            method: 'DELETE',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken
            })
        }).then(async (data) => {
            // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
            if (data.status !== 200) {
                throw new Error((await data.json()).status)
            }

            await fetchScoredScenes()
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.scoredScene.messages.delete.success',
                    defaultMessage: 'Video scene deleted.'
                }), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
        })
    }

    return {scoredScenes, loading, handleDeleteScoredScene, deletionAttemptedScoredScene, deletionInProgress}
}
export default useListScoredScenes;